import {UI} from "../../../stem-core/src/ui/UIBase";
import {StyleSheet} from "../../../stem-core/src/ui/Style";

import {DropdownItem} from "./DropdownItem";
import {styleRule} from "../../../stem-core/src/decorators/Style";
import {isSmallScreen} from "../../Utils";
import {registerStyle} from "../../../stem-core/src/ui/style/Theme";
import {isDeepEqual} from "../../UtilsLib";
import {KeyEvent} from "../../KeyEvent";


export class DropdownListStyle extends StyleSheet {
    transitionTime = 200;

    @styleRule
    list = {
        // TODO: Create a constants object with all of the floating elements' zIndexes so we have an idea of the
        //  overlapping.
        zIndex: 100005,
        maxHeight: 200,
        transition: "transform " + this.transitionTime + "ms ease",
        transformOrigin: "top",
        transform: "scale(1) translateY(10px)",
        opacity: 1,
        overflow: "auto",
        position: "fixed",
        width: "100%",
        padding: "10px 0",
        background: this.themeProps.DROPDOWN_EXPANDED_BACKGROUND,
        boxShadow: this.themeProps.DROPDOWN_EXPANDED_SHADOW,
        borderRadius: "6px",
        ">:first-child": {
            borderTop: "none",
        },
        ">*": {
            borderTop: () => "1px solid " + this.themeProps.DROPDOWN_ITEM_BORDER,
        },
    };

    @styleRule
    item = {
        width: "100%",
        color: this.themeProps.DROPDOWN_COLOR,
        whiteSpace: "nowrap",
        fontSize: () => (isSmallScreen() ? this.themeProps.MERCHANT_FONT_SIZE_NORMAL : this.themeProps.MERCHANT_FONT_SIZE_LARGE),
        ">*": {
            padding: () => (isSmallScreen() ? 7 : 13),
        },
    };

    @styleRule
    hidden = {
        pointerEvents: "none",
        transform: "scale(.9) translateY(-10px)",
        opacity: 0,
        position: "fixed",
    };

    @styleRule
    hovered = {
        background: this.themeProps.DROPDOWN_ITEM_HOVER_BACKGROUND,
    };
}


@registerStyle(DropdownListStyle)
export class DropdownList extends UI.Element {
    hidden = true;
    lastKeyUpTimestamp = 0;
    currentSelectedElementIndex = -1;
    prefix = "";
    interval = null;

    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            testId: "dropdownList",
        }
    }

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        if (this.hidden) {
            attr.addClass(this.styleSheet.hidden);
            return;
        }
        const {top, left, width} = this.options.dropdown.node.getBoundingClientRect();
        const bodyClientRect = document.body.getBoundingClientRect();
        const delta = Math.max(0, top + 210 - bodyClientRect.bottom);
        attr.setStyle({top: top - bodyClientRect.top - window.scrollY - delta,
            left: left - bodyClientRect.left - window.scrollX, width});
        attr.addClass(this.styleSheet.list);
    }

    onKeyUp(event) {
        event.stopPropagation();
        event.preventDefault();

        const key = new KeyEvent(event);

        if (key.isEnter() || key.isTab()) {
            this.children[this.currentSelectedElementIndex].children[0].setActive();
            this.collapse();
            return;
        }
        if (key.isBackspace()) {
            this.prefix = "";
            return;
        }
        if (key.isEscape()) {
            this.collapse();
            return;
        }
        // TODO: This doesn't work when scrolling is involved.
        if (key.isArrowUp()) {
            this.updateHoverElement(this.currentSelectedElementIndex - 1);
            return;
        }
        if (key.isArrowDown()) {
            this.updateHoverElement(this.currentSelectedElementIndex + 1);
            return;
        }

        const currentTimestamp = Date.now();

        // 1.5s delay, after that it resets.
        if (currentTimestamp - this.lastKeyUpTimestamp > 1000) {
            this.prefix = "";
        }
        this.lastKeyUpTimestamp = currentTimestamp;
        this.prefix += key.toString();

        this.updateCurrentHighlightedOption();
    }

    updateCurrentHighlightedOption() {
        let index = 0;
        for (const option of this.children) {
            const optionName = option.children[0].children[0].node.innerHTML;
            if (optionName.toLowerCase().startsWith(this.prefix.toLowerCase())) {
                this.updateHoverElement(index);
                return;
            }
            index += 1;
        }
    }

    updateHoverElement(index) {
        if (index < 0) {
            index = this.children.length - 1;
        } else if (index >= this.children.length) {
            index = 0;
        }
        if (this.currentSelectedElementIndex >= 0) {
            this.children[this.currentSelectedElementIndex].removeClass(this.styleSheet.hovered);
        }
        this.currentSelectedElementIndex = index;
        this.children[this.currentSelectedElementIndex].addClass(this.styleSheet.hovered);
        this.children[this.currentSelectedElementIndex].node.scrollIntoView();
    }

    formatOption(option) {
        return option;
    }

    render() {
        const {styleSheet} = this;
        const {options, active} = this.options;

        return options.map(option => {
                return <div className={styleSheet.item}>
                    <DropdownItem active={active && isDeepEqual(active, option)} list={this}>
                        {this.formatOption(option)}
                    </DropdownItem>
                </div>
            }
        );
    }

    static expand(dropdown, options, active) {
        return this.create(document.body, {dropdown, options, active});
    }

    collapse() {
        clearInterval(this.interval);
        this.hidden = true;
        this.redraw();

        setTimeout(() => {
            this.options.dropdown.dispatch("collapse");
            this.destroyNode();
        }, this.styleSheet.transitionTime);
    }

    addDropdownInteractions() {
        const {dropdown} = this.options;

        this.addListener("changeActive", child => {
            dropdown.dispatch("changeActive", child);
            this.updateOptions({active: child.value});
            this.collapse();
        });

        const {top, left, width, height} = dropdown.node.getBoundingClientRect();
        this.interval = this.attachInterval(() => {
            const rect = dropdown.node.getBoundingClientRect();
            if (rect.top !== top || rect.left !== left || rect.width !== width || rect.height !== height) {
                this.collapse();
            }
        }, 300);
    }

    scrollToActiveItem() {
        if (!this.children) {
            return;
        }

        const activeItem = this.children.find(itemWrapperNode => itemWrapperNode.children[0].options.active)

        if (activeItem) {
            activeItem.node.scrollIntoView({block: "center"})
        }
    }

    showDropdownList() {
        // While the window size changes, delay the apparition of the dropdown list. If there is a 50ms interval where
        // no reizes occured, then show the lis. After a second, if we weren't able to show the list due to these
        // window size changes, close the list.

        const deltaTimeout = 50;

        this.attachTimeout(() => {
            if (this.hidden) {
                this.collapse();
            }
        }, 1000);

        let lastChangeTimestamp = Date.now();
        this.attachTimeout(() => tryToShowDropdownList(lastChangeTimestamp), deltaTimeout);

        const tryToShowDropdownList = inputChangeTimestamp => {
            if (lastChangeTimestamp === inputChangeTimestamp) {
                this.hidden = false;
                this.redraw();
                this.scrollToActiveItem();
                this.addDropdownInteractions();
            }
        };
        this.attachEventListener(window, "resize", () => {
            lastChangeTimestamp = Date.now();
            this.attachTimeout(() => tryToShowDropdownList(lastChangeTimestamp), deltaTimeout);
        });
    }

    onMount() {
        super.onMount();

        this.showDropdownList();

        this.attachEventListener(document.body, "click", () => this.collapse());
        this.attachEventListener(document.body, "keyup", event => this.onKeyUp(event));
    }
}
